import { CategoryAvatarData, FacetFilter, SearchResponseDataCategories } from './category.service.interface'
import { FloApiSearchResponseData, SearchMeta, SearchQueryParams } from './search-data.service.interface'
import { ParamMap } from '@angular/router'
import { CategoryView, ViewAllLink } from '../libs/category-list/category-list.interface'
import { FacetsObject } from '../shared/facets/models'
import { VIEW_ALL_PER_PAGE_COUNT } from './category.service'

/**
 * Map array of API search responses (with their query string params & facets) to array of CategoryView objects,
 * which is used to populate the category-list.component. It also checks that response.data !== null per response; if
 * all response.data are null it returns an empty array.
 * and generates the header for each category
 */
export const mapSearchResponseToCategoryView = (data: SearchResponseDataCategories[]): ReadonlyArray<CategoryView> => {
  const categories = data
    .filter(cat => cat.response.data !== null)
    .map(dataCategory => {
      return {
        header: createHeaderFromFacets(dataCategory.queryParams?.facets),
        viewAll: createViewAllData(dataCategory.response.meta.pages, dataCategory.queryParams?.facets),
        items: mapAvatarData(dataCategory.response.data),
        meta: dataCategory.response.meta
      }
    })

  return categories
}

export const createViewAllData = (totalPages: number, facetString: string | undefined): ViewAllLink => {
  return {
    visible: totalPages > 1,
    text: 'View All',
    params: {
      facets: facetString
    }
  }
}

/**
 * Map over an array of search API responses to produce an array of CategoryItemData, which is used to populate each
 * individual Avatar component
 */
export const mapAvatarData = (data: readonly FloApiSearchResponseData[]): CategoryAvatarData[] => {
  return data.map(apiData => {
    return {
      id: apiData.id,
      primaryLabel: apiData.title,
      href: `${apiData.id}-${apiData.slug}`,
      image: apiData.asset_url
    }
  })
}

// Check the URL for user search query params, if found, parse, and add on additional properties.
export const getRouteQueryParams = (queryParamMap: ParamMap): SearchQueryParams => {
  const q = queryParamMap.get('q')
  const facets = queryParamMap.get('facets')
  const navId = queryParamMap.get('nav_id')
  const navIdAsParam = !!navId ? { nav_id: navId.toString() } : {}

  // Generic search page with no searching or facets or nav_id. Nothing to do.
  if (!q && !facets && !navId) return {}

  // If there's ONLY a navId, return just that.
  if (!q && !facets && navId) return navIdAsParam

  // IMPORTANT: Return empty strings by default, NOT undefined.
  // if you send undefined the api will search for string 'undefined'
  return {
    q: q || '',
    facets: facets ? decodeURIComponent(facets) : '',
    limit: VIEW_ALL_PER_PAGE_COUNT.toString(),
    user_preferences_only: 0,
    ...navIdAsParam
  }
}

// We need to check that there are, at minimum, items in the first view.
export const shouldShowViews = (views: readonly CategoryView[]): readonly CategoryView[] | undefined => {
  if (!views || !views[0] || !views[0].items.length) {
    return undefined
  }
  // If only one view (view-all), hide its view-all CTA
  if (views.length === 1) {
    views[0].viewAll.visible = false
  }
  return views
}

/**
 * For pagination/infinite scroll. If on page 1, only return the latest items
 * If page > 1, combine current items with previous page's items
 */
export const accumulateViewAll = (
  acc: ReadonlyArray<CategoryView> | undefined,
  curr: ReadonlyArray<CategoryView> | undefined
): ReadonlyArray<CategoryView> | undefined => {
  const accItems = (acc && acc[0]?.items) || []

  return curr && curr[0]?.meta?.page === 1
    ? curr
    : curr &&
        curr.map((c: CategoryView) => ({
          header: c.header,
          viewAll: c.viewAll,
          items: [...accItems, ...c.items],
          meta: c.meta
        }))
}

/**
 * Infinite scroll should only fire for teams' scrollable view
 * When query params are in the url
 * When current page is less than max number of pages
 * And when the meta is defined
 */
export const filterTeamsPaginationCriteria = (
  qp: SearchQueryParams,
  meta: SearchMeta | undefined,
  page: number
): boolean => {
  return Object.keys(qp).length !== 0 && meta !== undefined && page < meta.pages
}

/**
 * Generate an array of facet values for separate API calls, filtering on
 * the desired facets.
 * TODO: add hard limit for the number of API calls
 */
export const buildFacetFilters = (
  facets: FacetsObject,
  filters: Array<FacetFilter>
): Array<Record<FacetFilter, string>> => {
  // exit if no filters are provided
  if (filters.length < 1) return []

  // Prepare accumulator
  const categories: Array<Record<string, string>> = []

  // Separate first filter from the rest to avoid duplicates
  const [first, ...rest] = filters

  // Loop over each facet value in the first array, and add any values from the
  // rest of the filters. Then push to the accumulator.
  facets[first]?.forEach(facet => {
    // Prepare record
    const categoryRecord: Record<string, string> = {}

    // Assign value from first filter to record
    categoryRecord[first] = facet.value

    // if there is only one filter, then push to outer accumulator
    if (!rest.length) {
      categories.push(categoryRecord)
    } else {
      // Loop through remaining filters and add to record
      rest.forEach(restFilter => {
        // loop through facets of each filter
        facets[restFilter]?.forEach(innerFacet => {
          // Prepare inner record
          const restCategory: Record<string, string> = {}

          // add values to inner record
          restCategory[restFilter] = innerFacet.value
          const finalCategory = { ...categoryRecord, ...restCategory }

          // Finally, push to outer accumulator
          categories.push(finalCategory)
        })
      })
    }
  })

  return categories
}

// Convert a facet string ex: `{"Conference":"Big East","Gender":"Women"}` to "Big East Women"
export const createHeaderFromFacets = (facets: string | undefined): string | undefined => {
  if (!facets) return undefined
  try {
    const facetsObject = facets ? JSON.parse(facets) : undefined
    if (typeof facetsObject !== 'object') return undefined

    const facetValues: string[] = Object.values(facetsObject)
    return facetValues.reduce((acc: string, iterator: string) => `${acc} ${iterator}`)
  } catch (error) {
    return undefined
  }
}
